import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { CALENDLY_LINK, CONTACT_PHONE } from '../../utils/constants';

const Homepage = () => {
  const [activeLanguage, setActiveLanguage] = useState('en');
  const [activeFAQ, setActiveFAQ] = useState(null);

  const content = {
    en: {
      hero: {
        title: "Defensible Valuations. Investor‑Grade Decisions.",
        subtitle: "CVA & CPA-led valuation advisory for deals, disputes, and strategic decisions.",
        cta1: "Free Consultation",
      },
      trustMarkers: {
        cva: "CVA & CPA Certified",
        experience: "30+ Years Combined Experience",
        clients: "50+ Valuations"
      },
      valueBlocks: [
        {
          title: "Certified Expertise",
          desc: "CVA-certified professionals with court-nominable qualifications and deep market knowledge"
        },
        {
          title: "Transparent Approach",
          desc: "Clear methodology, defensible assumptions, and comprehensive documentation for every engagement"
        },
        {
          title: "Litigation-Ready Support",
          desc: "Prepared for expert testimony with defensible valuations and clear workpapers suitable for legal proceedings"
        }
      ],
      services: {
        title: "Featured Services",
        items: [
          { name: "Business Valuation", path: "/valuation", icon: "TrendingUp" },
          { name: "Litigation Support", path: "/services", icon: "Scale" },
          // { name: "Financial Reporting", path: "/services", icon: "FileText" },
          { name: "M&A Advisory", path: "/services", icon: "Handshake" }
        ]
      },
      // caseStudy: {
      //   title: "Recent Success",
      //   company: "GCC Manufacturing Company",
      //   outcome: "$12M Value Confirmed",
      //   description: "Successfully defended client\'s position in shareholder dispute through comprehensive valuation analysis"
      // },
      faqs: [
        {
          question: "What makes your valuations defensible in court?",
          answer: "Our CVA & CPA certification and adherence to NACVA & AICPA standards ensure our reports meet the highest professional standards and are admissible in legal proceeding."
        },
        {
          question: "How long does a typical valuation take?",
          answer: "Standard business valuations typically take 2-4 weeks depending on complexity. We provide detailed timelines during the initial consultation."
        },
        {
          question: "What types of valuations do you provide?",
          answer: "We specialize in business valuations for litigation, shareholder disputes, estate and gift tax matters, financial reporting, and transaction support"
        },
        {
          question: "Do you work with attorneys and CPA firms?",
          answer: "Yes. We regularly support law firms, accounting firms, and advisory practices that need reliable valuations for their clients."
        }, {
          question: "Can you provide expert testimony?",
          answer: "Yes. We can support attorneys through expert reports, depositions, and court testimony when required."
        }, {
          question: "Do you offer rebuttal or review services?",
          answer: "Yes. We can review opposing valuation reports and provide structured critique and rebuttal analysis when engaged by counsel."
        }
      ],
      contact: {
        title: "Ready to Get Started?",
        subtitle: "Connect with our CVA & CPA-certified experts",
        // whatsapp: "WhatsApp +973 3888 4499"
      }
    },
    ar: {
      hero: {
        title: "تقييمات قابلة للدفاع. قرارات على مستوى المستثمرين.",
        subtitle: "استشارات التقييم بقيادة CVA للصفقات والنزاعات والتقارير المالية في البحرين ودول مجلس التعاون الخليجي",
        cta1: "احجز استشارة",
      },
      trustMarkers: {
        cva: "معتمد CVA",
        experience: "أكثر من 12 سنة",
        clients: "أكثر من 280 تقييم"
      },
      valueBlocks: [
        {
          title: "خبرة معتمدة",
          desc: "محترفون معتمدون من CVA مع مؤهلات قابلة للترشيح للمحكمة ومعرفة عميقة بأسواق مجلس التعاون الخليجي"
        },
        {
          title: "نهج شفاف",
          desc: "منهجية واضحة وافتراضات قابلة للدفاع وتوثيق شامل لكل مشاركة"
        },
        {
          title: "التركيز على دول الخليج",
          desc: "خبرة إقليمية عبر البحرين والإمارات والسعودية وأسواق مجلس التعاون الخليجي الأخرى مع رؤى محلية"
        }
      ],
      services: {
        title: "الخدمات المميزة",
        items: [
          { name: "تقييم الأعمال", path: "/valuation", icon: "TrendingUp" },
          { name: "دعم التقاضي", path: "/services", icon: "Scale" },
          { name: "التقارير المالية", path: "/services", icon: "FileText" },
          { name: "استشارات الاندماج والاستحواذ", path: "/services", icon: "Handshake" }
        ]
      },
      caseStudy: {
        title: "نجاح حديث",
        company: "شركة تصنيع خليجية",
        outcome: "تأكيد قيمة 12 مليون دولار",
        description: "دافعنا بنجاح عن موقف العميل في نزاع المساهمين من خلال تحليل التقييم الشامل"
      },
      faqs: [
        {
          question: "ما الذي يجعل تقييماتكم قابلة للدفاع في المحكمة؟",
          answer: "شهادة CVA والالتزام بمعايير التقييم الدولية (IVS) يضمن أن تقاريرنا تلبي أعلى المعايير المهنية وقابلة للقبول في الإجراءات القانونية."
        },
        {
          question: "كم يستغرق التقييم النموذجي؟",
          answer: "تقييمات الأعمال القياسية تستغرق عادة 2-4 أسابيع حسب التعقيد. نقدم جداول زمنية مفصلة خلال الاستشارة الأولية."
        },
        {
          question: "هل تعملون في جميع دول مجلس التعاون الخليجي؟",
          answer: "نعم، لدينا خبرة واسعة عبر البحرين والإمارات والسعودية والكويت وعمان وقطر مع فهم عميق للوائح المحلية وظروف السوق."
        }
      ],
      contact: {
        title: "مستعد للبدء؟",
        subtitle: "تواصل مع خبرائنا المعتمدين من CVA",
        whatsapp: "واتساب +973 1234 5678"
      }
    }
  };

  const currentContent = content?.[activeLanguage];

  return (
    <>
      <Helmet>
        <title>Northbridge Valuation & Advisory | Defensible Valuations</title>
        <meta name="description" content="Defensible Valuations. Investor‑Grade Decisions. CVA-certified valuation advisory for deals, disputes, and financial reporting." />
        <meta name="keywords" content="business valuation, CVA certified valuers, litigation support valuation, financial reporting valuation, Northbridge Valuation" />
        <link rel="canonical" href="/" />
      </Helmet>
      <div className={`min-h-screen bg-background ${activeLanguage === 'ar' ? 'rtl' : 'ltr'}`} dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}>
        <Header />

        {/* Language Toggle */}
        {/* <div className="fixed top-20 left-6 z-30">
          <div className="bg-white/95 backdrop-blur-sm border border-border rounded-lg p-1 shadow-brand">
            <button
              onClick={() => setActiveLanguage('en')}
              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                activeLanguage === 'en' ? 'bg-accent text-white' : 'text-text-secondary hover:text-accent'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setActiveLanguage('ar')}
              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                activeLanguage === 'ar' ? 'bg-accent text-white' : 'text-text-secondary hover:text-accent'
              }`}
            >
              العربية
            </button>
          </div>
        </div> */}

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/5 via-white to-accent/5 pt-8 pb-16">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23e2e8f0%22%20fill-opacity%3D%220.3%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-accent/10 rounded-full mb-8">
                <Icon name="TrendingUp" size={40} className="text-accent" />
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
                {currentContent?.hero?.title}
              </h1>

              <p className="text-xl text-text-secondary max-w-4xl mx-auto mb-8 leading-relaxed">
                {currentContent?.hero?.subtitle}
              </p>

              <div className="flex justify-center mb-12">
                <Button
                  variant="default"
                  size="xl"
                  iconName="Calendar"
                  iconPosition="left"
                  asChild
                >
                  <Link to="/book-consultation">
                    {currentContent?.hero?.cta1}
                  </Link>
                </Button>
              </div>

              {/* Trust Markers */}
              <div className="grid sm:grid-cols-3 gap-8 max-w-2xl mx-auto">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-3">
                    <Icon name="Award" size={24} className="text-success" />
                  </div>
                  <span className="font-semibold text-primary">{currentContent?.trustMarkers?.cva}</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-3">
                    <Icon name="Clock" size={24} className="text-accent" />
                  </div>
                  <span className="font-semibold text-primary">{currentContent?.trustMarkers?.experience}</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mb-3">
                    <Icon name="Users" size={24} className="text-warning" />
                  </div>
                  <span className="font-semibold text-primary">{currentContent?.trustMarkers?.clients}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Blocks */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {currentContent?.valueBlocks?.map((block, index) => (
                <div key={index} className="text-center group hover-lift">
                  <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-accent/20 transition-colors">
                    <Icon
                      name={['Shield', 'Eye', 'MapPin']?.[index]}
                      size={28}
                      className="text-accent"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-4">{block?.title}</h3>
                  <p className="text-text-secondary leading-relaxed">{block?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-16 bg-muted">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-primary mb-4">{currentContent?.services?.title}</h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                Comprehensive valuation services for a wide range of business scenarios and regulatory frameworks.              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {currentContent?.services?.items?.map((service, index) => (
                <Link key={index} to={service?.path} className="block group">
                  <div className="bg-white rounded-xl p-6 shadow-brand hover:shadow-brand-lg transition-all duration-300 group-hover:-translate-y-1">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                      <Icon name={service?.icon} size={24} className="text-accent" />
                    </div>
                    <h3 className="font-semibold text-primary mb-2">{service?.name}</h3>
                    <div className="flex items-center text-accent group-hover:text-accent-dark transition-colors">
                      <span className="text-sm font-medium">Learn More</span>
                      <Icon name="ArrowRight" size={16} className={`${activeLanguage === 'ar' ? 'ml-2 rotate-180' : 'ml-2'}`} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Mini Case Study */}
        {/* <section className="py-16 bg-primary text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Icon name="CheckCircle" size={32} className="text-white" />
            </div>
            
            <h2 className="text-3xl font-bold mb-4">{currentContent?.caseStudy?.title}</h2>
            <div className="bg-white/10 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold mb-2">{currentContent?.caseStudy?.company}</h3>
              <div className="text-3xl font-bold text-accent mb-4">{currentContent?.caseStudy?.outcome}</div>
              <p className="text-white/90">{currentContent?.caseStudy?.description}</p>
            </div>
            
            <Button
              variant="secondary"
              size="lg"
              iconName="FileText"
              iconPosition="left"
            >
              View All Case Studies
            </Button>
          </div>
        </section> */}

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-text-secondary">
                Get answers to common questions about our valuation services
              </p>
            </div>

            <div className="space-y-4">
              {currentContent?.faqs?.map((faq, index) => (
                <div key={index} className="border border-border rounded-lg">
                  <button
                    onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <span className="font-semibold text-primary">{faq?.question}</span>
                    <Icon
                      name="ChevronDown"
                      size={20}
                      className={`text-accent transition-transform ${activeFAQ === index ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {activeFAQ === index && (
                    <div className="px-6 pb-4 text-text-secondary leading-relaxed">
                      {faq?.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Strip */}
        <section className="py-12 bg-accent text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold mb-2">{currentContent?.contact?.title}</h2>
            <p className="text-accent-foreground/90 mb-6">{currentContent?.contact?.subtitle}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* <a
                href="https://wa.me/97338884499"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-success hover:bg-success/90 text-white font-semibold rounded-lg transition-colors"
              >
                <Icon name="MessageCircle" size={20} className={`${activeLanguage === 'ar' ? 'ml-2' : 'mr-2'}`} />
                {currentContent?.contact?.whatsapp}
              </a> */}
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-accent"
                iconName="Phone"
                iconPosition="left"
                asChild
              >
                <a href={`tel:${CONTACT_PHONE}`}>Call {CONTACT_PHONE}</a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Homepage;